// src/components/layout/NavBar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { FiSun, FiMoon, FiLogOut, FiMenu, FiX, FiMessageSquare, FiHome, FiSearch, FiGrid, FiSettings, FiFileText, FiChevronDown, FiTool, FiActivity, FiUsers, FiCalendar } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from './NotificationBell';

const Navbar = ({ openLoginModal, openSignupModal }) => {
    const { user, isAuthenticated, logout } = useAuth();
    const location = useLocation();
    const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const profileRef = useRef(null);

    // Import from API inside effect or dynamically to avoid cycle if any, but regular import is at top.
    // For now we will do a dynamic import inside the effect.
    useEffect(() => {
        let interval;
        if (isAuthenticated && user?.id) {
            const fetchUnread = async () => {
                try {
                    const { getUnreadMessageCount } = await import('../../services/api');
                    const data = await getUnreadMessageCount(user.id);
                    setUnreadCount(data.unread_count || 0);
                } catch (e) {
                    console.error("Error fetching unread count");
                }
            };
            fetchUnread();
            interval = setInterval(fetchUnread, 15000); // 15 seconds
        }
        return () => {
            if (interval) clearInterval(interval);
        }
    }, [isAuthenticated, user]);

    useEffect(() => {
        document.documentElement.classList.toggle('dark', isDarkMode);
        localStorage.setItem('darkMode', isDarkMode);
    }, [isDarkMode]);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close profile dropdown on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Close mobile menu on route change
    useEffect(() => { setMobileOpen(false); setProfileOpen(false); }, [location.pathname]);

    const handleLogin = () => {
        if (openLoginModal) openLoginModal();
        setMobileOpen(false);
    };

    const isActive = (path) => location.pathname === path;

    const NavLink = ({ to, children, icon: Icon }) => (
        <Link
            to={to}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                ${isActive(to)
                    ? 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/10'
                    : 'text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white hover:bg-surface-100 dark:hover:bg-surface-800'
                }`}
        >
            {Icon && <Icon className="w-4 h-4" />}
            {children}
        </Link>
    );

    const displayName = user?.displayName || user?.email?.split('@')[0] || 'Usuario';
    const initials = displayName.charAt(0).toUpperCase();

    return (
        <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled
            ? 'bg-white/80 dark:bg-surface-950/80 backdrop-blur-xl shadow-sm border-b border-surface-200/60 dark:border-surface-800/60'
            : 'bg-white dark:bg-surface-950 border-b border-surface-100 dark:border-surface-900'
            }`}>
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex justify-between items-center">

                {/* Logo */}
                <Link to="/" aria-label="Inicio - Hoommy" className="flex items-center gap-2.5 group shrink-0">
                    <img src={logo} alt="Hoommy" className="h-10 w-20 transition-transform duration-300 group-hover:scale-105" width={80} height={40} />
                    <span className="hidden sm:block text-lg font-bold text-surface-900 dark:text-white tracking-tight">
                        Hoommy
                    </span>
                </Link>

                {/* Desktop nav — center links */}
                <div className="hidden md:flex items-center gap-1 mx-6">
                    <NavLink to="/" icon={FiSearch}>Explorar</NavLink>
                    <NavLink to="/estudiantes" icon={FiHome}>Estudiantes</NavLink>
                    {isAuthenticated && (
                        <>
                            <Link
                                to="/messages"
                                className={`relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                                    ${isActive('/messages')
                                        ? 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/10'
                                        : 'text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white hover:bg-surface-100 dark:hover:bg-surface-800'
                                    }`}
                            >
                                <FiMessageSquare className="w-4 h-4" />
                                Mensajes
                                {unreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-sm ring-2 ring-white dark:ring-surface-950">
                                        {unreadCount > 99 ? '99+' : unreadCount}
                                    </span>
                                )}
                            </Link>
                            <NavLink to="/dashboard" icon={FiGrid}>Mi Panel</NavLink>
                        </>
                    )}
                </div>

                {/* Right section */}
                <div className="flex items-center gap-2">
                    {/* Notifications — solo desktop */}
                    {isAuthenticated && <span className="hidden md:block"><NotificationBell /></span>}

                    {/* Dark mode */}
                    <button
                        onClick={() => setIsDarkMode(!isDarkMode)}
                        className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                        aria-label="Cambiar tema"
                    >
                        {isDarkMode
                            ? <FiSun className="w-[18px] h-[18px] text-amber-400" />
                            : <FiMoon className="w-[18px] h-[18px] text-surface-500" />
                        }
                    </button>

                    {/* Desktop: Avatar dropdown or Login */}
                    <div className="hidden md:block" ref={profileRef}>
                        {isAuthenticated ? (
                            <div className="relative">
                                <button
                                    onClick={() => setProfileOpen(!profileOpen)}
                                    aria-label="Menú de perfil"
                                    aria-expanded={profileOpen}
                                    className="flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-xl hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                                >
                                    {user?.photoURL ? (
                                        <img src={user.photoURL} alt="" className="w-8 h-8 rounded-lg object-cover" />
                                    ) : (
                                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white text-sm font-bold">
                                            {initials}
                                        </div>
                                    )}
                                    <span className="text-sm font-medium text-surface-700 dark:text-surface-300 max-w-[120px] truncate">{displayName}</span>
                                    {user?.es_estudiante_verificado && <span className="text-sm leading-none">🎓</span>}
                                    <FiChevronDown className={`w-3.5 h-3.5 text-surface-400 transition-transform duration-200 ${profileOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown */}
                                {profileOpen && (
                                    <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-surface-900 rounded-xl shadow-xl shadow-black/10 dark:shadow-black/30 border border-surface-200 dark:border-surface-800 py-2 animate-fade-in-up z-50">
                                        <div className="px-4 py-2.5 border-b border-surface-100 dark:border-surface-800">
                                            <p className="text-sm font-semibold text-surface-900 dark:text-white truncate">{displayName}</p>
                                            <p className="text-xs text-surface-500 truncate">{user?.email}</p>
                                        </div>
                                        <div className="py-1">
                                            <Link to="/dashboard" className="flex items-center gap-2.5 px-4 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors">
                                                <FiGrid className="w-4 h-4 text-surface-400" /> Mi Panel
                                            </Link>
                                            <Link to="/settings" className="flex items-center gap-2.5 px-4 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors">
                                                <FiSettings className="w-4 h-4 text-surface-400" /> Configuración
                                            </Link>
                                            <Link to="/contracts" className="flex items-center gap-2.5 px-4 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors">
                                                <FiFileText className="w-4 h-4 text-surface-400" /> Mis Contratos
                                            </Link>
                                            {(user?.rol === 'arrendador' || user?.rol === 'inmobiliaria' || user?.rol === 'admin') && (
                                                <Link to="/maintenance/owner" className="flex items-center gap-2.5 px-4 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors">
                                                    <span className="w-4 h-4 flex items-center justify-center text-surface-400">🔧</span> Mantenimiento
                                                </Link>
                                            )}
                                            <Link to="/dashboard?tab=visits" className="flex items-center gap-2.5 px-4 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors">
                                                <span className="w-4 h-4 flex items-center justify-center text-surface-400">📅</span> Mis Visitas
                                            </Link>
                                            {(user?.rol === 'admin' || user?.rol === 'arrendador') && (
                                                <>
                                                    <Link to="/admin/earnings" className="flex items-center gap-2.5 px-4 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors">
                                                        <span className="w-4 h-4 flex items-center justify-center text-surface-400">📈</span> Ganancias
                                                    </Link>
                                                    <Link to="/admin/providers" className="flex items-center gap-2.5 px-4 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors">
                                                        <span className="w-4 h-4 flex items-center justify-center text-surface-400">👷</span> Proveedores
                                                    </Link>
                                                </>
                                            )}
                                            {(user?.rol === 'arrendador' || user?.rol === 'inmobiliaria') && (
                                                <>
                                                    <Link to="/publish" className="flex items-center gap-2.5 px-4 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors">
                                                        <FiHome className="w-4 h-4 text-surface-400" /> Publicar propiedad
                                                    </Link>
                                                    <Link to="/planes" className="flex items-center gap-2.5 px-4 py-2 text-sm text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-500/10 transition-colors font-medium">
                                                        <span className="w-4 h-4 flex items-center justify-center text-orange-500">⚡</span> Mi Plan
                                                    </Link>
                                                </>
                                            )}
                                        </div>
                                        <div className="border-t border-surface-100 dark:border-surface-800 pt-1">
                                            <button
                                                onClick={() => { logout(); setProfileOpen(false); }}
                                                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                            >
                                                <FiLogOut className="w-4 h-4" /> Cerrar sesión
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button
                                className="flex items-center gap-2 btn-primary py-2 px-5 text-sm rounded-xl font-semibold"
                                onClick={handleLogin}
                            >
                                Iniciar Sesión
                            </button>
                        )}
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        onClick={() => setMobileOpen(!mobileOpen)}
                        className="md:hidden p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                        aria-label="Abrir menú"
                    >
                        {mobileOpen
                            ? <FiX className="w-5 h-5 text-surface-700 dark:text-surface-300" />
                            : <FiMenu className="w-5 h-5 text-surface-700 dark:text-surface-300" />
                        }
                    </button>
                </div>
            </nav>

            {/* Mobile menu */}
            <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${mobileOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="border-t border-surface-100 dark:border-surface-800 bg-white dark:bg-surface-950 px-4 py-4 space-y-1">

                    {/* User info (mobile) */}
                    {isAuthenticated && (
                        <div className="flex items-center gap-3 px-3 py-3 mb-2 rounded-xl bg-surface-50 dark:bg-surface-900">
                            {user?.photoURL ? (
                                <img src={user.photoURL} alt="" className="w-10 h-10 rounded-xl object-cover" />
                            ) : (
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white font-bold">
                                    {initials}
                                </div>
                            )}
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-surface-900 dark:text-white truncate flex items-center gap-1.5">
                                    {displayName}
                                    {user?.es_estudiante_verificado && <span className="text-sm">🎓</span>}
                                </p>
                                <p className="text-xs text-surface-500 truncate">{user?.email}</p>
                            </div>
                            <NotificationBell />
                        </div>
                    )}

                    {/* Links */}
                    <MobileLink to="/" icon={FiSearch} label="Explorar" active={isActive('/')} />
                    <MobileLink to="/estudiantes" icon={FiHome} label="Para Estudiantes" />
                    {isAuthenticated && (
                        <>
                            <MobileLink to="/messages" icon={FiMessageSquare} label="Mensajes" active={isActive('/messages')} />
                            <MobileLink to="/dashboard" icon={FiGrid} label="Mi Panel" active={isActive('/dashboard')} />
                            <MobileLink to="/contracts" icon={FiFileText} label="Mis Contratos" active={isActive('/contracts')} />
                            <MobileLink to="/dashboard?tab=visits" icon={FiCalendar} label="Mis Visitas" active={isActive('/dashboard?tab=visits')} />
                            <MobileLink to="/settings" icon={FiSettings} label="Configuración" active={isActive('/settings')} />
                            {(user?.rol === 'arrendador' || user?.rol === 'inmobiliaria' || user?.rol === 'admin') && (
                                <MobileLink to="/maintenance/owner" icon={FiTool} label="Mantenimiento" active={isActive('/maintenance/owner')} />
                            )}
                            {(user?.rol === 'admin' || user?.rol === 'arrendador') && (
                                <>
                                    <MobileLink to="/admin/earnings" icon={FiActivity} label="Ganancias" active={isActive('/admin/earnings')} />
                                    <MobileLink to="/admin/providers" icon={FiUsers} label="Proveedores" active={isActive('/admin/providers')} />
                                </>
                            )}
                            {(user?.rol === 'arrendador' || user?.rol === 'inmobiliaria') && (
                                <MobileLink to="/publish" icon={FiHome} label="Publicar propiedad" active={isActive('/publish')} />
                            )}
                        </>
                    )}

                    {/* Auth actions */}
                    <div className="border-t border-surface-100 dark:border-surface-800 pt-3 mt-2">
                        {isAuthenticated ? (
                            <button
                                onClick={() => { logout(); setMobileOpen(false); }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 font-medium text-sm transition-colors"
                            >
                                <FiLogOut className="w-4 h-4" /> Cerrar sesión
                            </button>
                        ) : (
                            <button
                                className="w-full btn-primary py-2.5 text-sm rounded-xl font-semibold"
                                onClick={handleLogin}
                            >
                                Iniciar Sesión
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

const MobileLink = ({ to, icon: Icon, label, active }) => (
    <Link
        to={to}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${active
            ? 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/10'
            : 'text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800'
            }`}
    >
        <Icon className="w-4 h-4" />
        {label}
    </Link>
);

export default Navbar;
